import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocaleState, useRedirect } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { makeStyles } from '@mui/styles'
import { getPropertyList, getPropertyDetail, setDefaultBuilding } from '@api'
import { selectedGroup, selectedProperty } from '@reducer/profile'
import { getImpersonation } from '@common/auth'
import { resolveBuildingCandidate } from '@/common/buildingCandidate'
import { compareText } from '@utils/sort'
import { IEntity } from '../types'
import ExpandMore from '@mui/icons-material/ExpandMore'
import classnames from 'classnames'

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textField: {
    height: 48,
    backgroundColor: 'white',
    '&[class*=MuiOutlinedInput-root]': {
      padding: '0 9px',
    },
  },
  label: {
    transform: 'translate(14px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
}))

interface SearchProps {
  style?: React.CSSProperties
  isBarSearch?: boolean
  label?: string
  gId?: string
  bId?: string
  onChange?: (property: IEntity) => void
  className?: string
  disableClearable?: boolean
  inputRootClass?: string
  labelClass?: string
  variant?: TextFieldProps['variant']
  allowedBuildingIds?: string[]
  includeAllOption?: boolean
  filterByManagementTag?: boolean
  onEntitiesLoaded?: (entities: IEntity[]) => void
}

const ALL_PROPERTY_OPTION = { id: '', Name: { en: 'All', es: 'All' } } as unknown as IEntity
// Building-specific editors that live outside the /b/:bId scope; after a
// switch their record belongs to the old building, so land on the new
// building's library (their parent section) instead.
const BUILDING_FLOW_PREFIXES = ['/qrcode/', '/twoQREdit', '/propertyQrGenerator', '/roundsEngineBuilder']
/**
 * Where to go when the selected building changes: stay on the current page.
 * Scoped paths swap the :bId segment in place (keeping tab/report/query),
 * building-specific editors fall back to the section root, and everything
 * else (home, account pages) needs no navigation at all.
 */
const pathForBuildingSwitch = (pathname: string, search: string, id: string) => {
  if (pathname.startsWith('/b/')) {
    return `/b/${id}${pathname.replace(/^\/b\/[^/]+/, '')}${search}`
  }
  if (BUILDING_FLOW_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return `/b/${id}/library`
  }
  return ''
}
const Search = (props: SearchProps) => {
  const {
    style = {},
    isBarSearch,
    gId,
    bId,
    onChange,
    label,
    className = '',
    inputRootClass,
    disableClearable = true,
    labelClass,
    variant = 'outlined',
    allowedBuildingIds,
    includeAllOption,
    filterByManagementTag,
    onEntitiesLoaded,
  } = props
  const {
    user,
    isCreateBuilding,
    buildingsVersion,
    selectedGroupId,
    selectedPropertyId,
    selectedPropertyName,
    selectedManagementTag,
  } = useSelector((state) => state.profile)
  const [loading, setLoading] = useState(true)
  const groupId = typeof gId === 'undefined' ? selectedGroupId || user?.UserContext?.CurrentGroupId : gId
  const propertyId = typeof bId === 'undefined' ? bId || selectedPropertyId : bId
  const [entities, setEntities] = React.useState<IEntity[]>([])
  const [disabled, setDisabled] = React.useState(false)
  const allPropertiesRef = useRef<IEntity[]>([])
  const [locale] = useLocaleState()
  const redirectTo = useRedirect()
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()
  const classes = useStyles()
  /**
   * Record the pick so the next session opens here. Fire-and-forget: this is a
   * convenience, and a failed write must never block the switch the user asked
   * for. The building the user just chose came from their own permitted list,
   * and the backend re-checks it anyway.
   *
   * Skipped during impersonation: the backend denies it for AcAdmins who are
   * only a Visitor in the target's group, and when it does succeed it writes
   * the preference onto the impersonated TARGET user's record, not the actor's.
   */
  const rememberBuilding = (propertyId?: string) => {
    if (!propertyId || propertyId === selectedPropertyId || getImpersonation()) return
    setDefaultBuilding({ BuildingId: propertyId }).catch(() => {
      /* preference is best-effort */
    })
  }

  // A session can end up with a building selected but no redux group
  // (impersonation, cross-group browsing). Repair it from the building's own
  // record whenever one gets selected, mirroring BuildingScope's cross-group
  // dispatch — never overrides a group that's already selected.
  const repairGroup = (entity: IEntity) => {
    if (!selectedGroupId && entity.GroupId) {
      dispatch(selectedGroup({ groupId: entity.GroupId, groupName: '', group: {} }))
    }
  }

  // Commit a single building as the active selection — shared by the normal
  // one-building auto-select and the token fallback below so both behave
  // identically (disable the picker, dispatch, follow the current page).
  const selectBuilding = (entity: IEntity) => {
    if (onChange) {
      onChange(entity)
      return
    }
    setDisabled(true)
    const name = entity.Name?.[locale] || ''
    repairGroup(entity)
    dispatch(
      selectedProperty({
        propertyId: entity.id,
        propertyName: name,
        isOneProperty: true,
        buildingCode: entity.Code,
        propertyTag: entity.PropertyTag || {},
      })
    )
    const target = pathForBuildingSwitch(pathname, search, entity.id)
    if (target) redirectTo(target)
  }

  // When the property list comes back empty or the fetch fails, building-scoped
  // roles (no ViewCompanyAllBuildings) have nothing to pick and every scoped
  // link dead-ends. Their identity is already in the token, so resolve the one
  // building from it and select it. Only auto-picks when the candidate is
  // unambiguous — a multi-building member with a failed fetch is left to retry
  // (see onOpen), never guessed.
  const selectFromToken = async (gId?: string) => {
    if (selectedPropertyId) return
    const candidate = resolveBuildingCandidate(user, allowedBuildingIds)
    if (!candidate) return
    let detail: IEntity | undefined
    try {
      detail = await getPropertyDetail<IEntity>({ eId: candidate, gId })
    } catch (e) {
      return
    }
    if (!detail || detail.Status === 'Archived') return
    const entity = { ...detail, id: candidate, acId: candidate } as IEntity
    allPropertiesRef.current = [entity]
    setEntities([entity])
    onEntitiesLoaded?.([entity])
    selectBuilding(entity)
  }

  const fetchProperties = async (gId?: string) => {
    if (!gId) {
      setLoading(false)
      return
    }
    setLoading(true)
    let res: { Properties: IEntity[]; Total: number } | null = null
    try {
      res = await getPropertyList<{ Properties: IEntity[]; Total: number }>({
        filter: JSON.stringify({ gId }),
        pagination: JSON.stringify({ page: 0, perPage: 999 }),
      })
    } catch (e) {
      // A rejected fetch must still resolve `loading`: a stuck spinner here
      // also blocks the auto-select below, which dead-ends every building-scoped
      // link until the page is reloaded. Reopening the picker retries (onOpen).
      res = null
    }
    setLoading(false)
    if (res) {
      const { Properties } = res
      allPropertiesRef.current = Properties
      const filtered =
        allowedBuildingIds && allowedBuildingIds.length > 0
          ? Properties.filter((p) => allowedBuildingIds.includes(p.id))
          : Properties
      setEntities(filtered)
      onEntitiesLoaded?.(filtered)
      setDisabled(false)
      if (filtered.length === 1) {
        selectBuilding(filtered[0])
        return
      }
      if (filtered.length > 0) return
    }
    // Empty list (backend denies building-scoped roles with no AccessBuildingIds)
    // or failed fetch: fall back to the building carried on the token.
    await selectFromToken(gId)
  }

  useEffect(() => {
    fetchProperties(groupId)
  }, [groupId])
  useEffect(() => {
    if (!isCreateBuilding) return
    fetchProperties(groupId)
  }, [groupId, isCreateBuilding])
  // A building save changes what this list holds — a renamed building, and the Management
  // tag the navbar's dropdown derives from these very entities. isCreateBuilding cannot
  // carry that: it latches true on the first save of a session and then stops changing, so
  // every later save left the list (and the tag dropdown built from it) stale until reload.
  // The ref keeps a counter restored from persisted state from re-fetching on mount.
  const seenBuildingsVersion = useRef(buildingsVersion)
  useEffect(() => {
    if (buildingsVersion === seenBuildingsVersion.current) return
    seenBuildingsVersion.current = buildingsVersion
    fetchProperties(groupId)
  }, [buildingsVersion])
  useEffect(() => {
    if (allPropertiesRef.current.length === 0) return
    const filtered =
      allowedBuildingIds && allowedBuildingIds.length > 0
        ? allPropertiesRef.current.filter((p) => allowedBuildingIds.includes(p.id))
        : allPropertiesRef.current
    setEntities(filtered)
    onEntitiesLoaded?.(filtered)
  }, [allowedBuildingIds])

  const managementTagId = (filterByManagementTag && selectedManagementTag?.TagId) || ''
  useEffect(() => {
    if (!managementTagId || loading || !selectedPropertyId) return
    const current = entities.find((e) => e.id === selectedPropertyId)
    if (current && current.ManagementTag?.TagId !== managementTagId) {
      dispatch(selectedProperty({ propertyId: '', propertyName: '', buildingCode: '', propertyTag: {} }))
    }
  }, [managementTagId, loading, selectedPropertyId, entities, dispatch])

  const option =
    (includeAllOption && propertyId === '' ? ALL_PROPERTY_OPTION : undefined) ||
    entities?.find((v) => v.id === propertyId) ||
    (loading && selectedPropertyId
      ? ({
          id: selectedPropertyId,
          Name: { [locale]: selectedPropertyName || '', en: selectedPropertyName || '' },
        } as unknown as IEntity)
      : undefined)
  // Alphabetical by the name actually shown, so a locale with no translated
  // name still orders by what the user reads. The pinned "All" entry is
  // prepended after the sort and never participates in it.
  const tagFilteredEntities = useMemo(() => {
    const filtered = managementTagId
      ? entities.filter((e) => e.ManagementTag?.TagId === managementTagId)
      : entities
    return [...filtered].sort((a, b) => compareText(a.Name?.[locale] || a.Name?.en, b.Name?.[locale] || b.Name?.en))
  }, [entities, managementTagId, locale])
  const optionList = includeAllOption ? [ALL_PROPERTY_OPTION, ...tagFilteredEntities] : tagFilteredEntities
  return (
    <Autocomplete
      id="free-solo-2-demo"
      disableClearable={disableClearable}
      disabled={disabled}
      loading={loading}
      onOpen={() => {
        if (!loading && entities.length === 0) fetchProperties(groupId)
      }}
      className={className}
      classes={{ inputRoot: `${classes.textField}  ${inputRootClass}` }}
      onChange={(_, Property) => {
        // Only an explicit pick is worth remembering — the auto-select paths
        // read the preference, so writing there would just echo it back.
        rememberBuilding((Property as IEntity)?.id)
        if (onChange) {
          // @ts-ignore
          return onChange(Property)
        }
        const { id, Name, Code, PropertyTag = {} } = Property as IEntity
        repairGroup(Property as IEntity)
        dispatch(
          selectedProperty({ propertyId: id, propertyName: Name[locale], buildingCode: Code, propertyTag: PropertyTag })
        )
        // Stay where the user is: swap the building in the URL rather than
        // yanking them to Building Info.
        const target = pathForBuildingSwitch(pathname, search, id)
        if (target) redirectTo(target)
      }}
      style={style}
      options={optionList}
      // @ts-ignore
      value={option || null}
      getOptionLabel={(option) => `${option.Name?.[locale]}` || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ classes: { root: classnames(classes.label, labelClass) } }}
          label={label}
          placeholder="Select Building"
          variant={variant}
        />
      )}
      popupIcon={variant !== 'standard' ? <ExpandMore /> : undefined}
    />
  )
}

export const PropertySearch = React.memo(Search)
